import { createRootRoute, Outlet, useRouterState, Link } from '@tanstack/react-router';
import { Toaster, toast } from 'sonner';
import { useEffect, useState } from 'react';
import { initTelegram, haptic, getWebApp } from '@/lib/telegram';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { LayoutDashboard, Activity, Zap, User, Megaphone, Check, Star } from 'lucide-react';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { queryClient, persister } from '@/lib/query-client';
import { APP_VERSION } from '@/lib/version';
import { useKeyboardOpen } from '@/lib/use-keyboard-open';
import { initAnalytics, track } from '@/lib/analytics';
import { api, ApiError, BotDisabledError, TelegramUnavailableError, currentBotId } from '@/lib/api';
import { BotDisabledScreen } from '@/components/BotDisabledScreen';
import { WebappDisabledScreen } from '@/components/WebappDisabledScreen';
import { TelegramUnavailableScreen } from '@/components/TelegramUnavailableScreen';
import { ErrorReportSheet } from '@/components/ErrorReportSheet';
import { autoLog } from '@/lib/error-report';

export const Route = createRootRoute({
  component: RootComponent,
});

/** Верх стопки тостов: под вырезом экрана и под шапкой Telegram, как у страницы. */
const TOAST_TOP =
  'calc(max(env(safe-area-inset-top), var(--tg-safe-area-inset-top, 0px)) + var(--tg-content-safe-area-inset-top, 0px) + 8px)';

// Анти-спам: один 5xx-toast не чаще раза в 5с (refetch'и могут лупить ошибку).
// Тост висит бесконечно (duration: Infinity) с кнопкой «Сообщить» — у юзера есть
// время нажать и отправить отчёт. openSheet прокидывается из компонента (доступ к state).
let lastServerErrorToast = 0;
function notifyServerError(error: unknown, openSheet: (e: unknown) => void) {
  const now = Date.now();
  if (now - lastServerErrorToast < 5000) return;
  lastServerErrorToast = now;
  toast.error(i18n.t('Backend down'), {
    description: i18n.t('Backend down desc'),
    duration: Infinity,
    action: {
      label: i18n.t('Report it'),
      onClick: () => openSheet(error),
    },
  });
}

function RootComponent() {
  const { t } = useTranslation();
  const keyboardOpen = useKeyboardOpen();
  // Bot отключён партнёром / удалён / JWT протух (401/403). Подписка на queryCache
  // ловит BotDisabledError даже из background-refetch'а — без этого экран показывал
  // только React ErrorBoundary, а до него ошибка из useQuery не доходила.
  const [botDisabled, setBotDisabled] = useState(false);
  const [webappOff, setWebappOff] = useState(false);
  // Telegram не отдал данные для входа (SDK не загрузился / пустая initData).
  const [tgError, setTgError] = useState<TelegramUnavailableError | null>(null);
  const [subGate, setSubGate] = useState<{ url?: string; name?: string }[] | null>(null);
  // Ошибка, по которой открыта форма отчёта (из persistent-тоста). null = закрыта.
  const [reportSheetError, setReportSheetError] = useState<unknown>(null);

  useEffect(() => {
    initTelegram();
    initAnalytics();
    track('app_opened', { platform: getWebApp()?.platform });

    const handleError = (error: unknown) => {
      // Данные от Telegram не пришли — запрос до бэка не дошёл, в серверных логах такой
      // случай не виден вообще. Шлём маячок (эндпоинт принимает и без токена), иначе мы
      // о таких отказах не узнаём никак.
      if (error instanceof TelegramUnavailableError) {
        setTgError(error);
        void autoLog(error, { severity: 'error' });
        return;
      }
      if (error instanceof BotDisabledError) {
        const err = (error.body as { error?: { code?: string; context?: { channels?: { url?: string; name?: string }[] } } } | null)?.error;
        if (err?.code === 'app.subscription_required') {
          setSubGate(err.context?.channels ?? []);
          return;
        }
        // Бот жив, партнёр выключил только мини-апп: свой экран, а не «бот недоступен».
        if (err?.code === 'app.webapp_disabled') {
          setWebappOff(true);
          return;
        }
        setBotDisabled(true);
        return;
      }
      // 5xx / сетевой сбой (status 0) — бэк лёг. Тихо логируем на бэк (авто-лог с
      // дедупом) + показываем persistent-toast с кнопкой «Сообщить».
      if (error instanceof ApiError && (error.status >= 500 || error.status === 0)) {
        void autoLog(error, { severity: 'error' });
        notifyServerError(error, setReportSheetError);
      }
    };

    // Просканировать уже осевшие в кэше ошибки (prefetch'и из main.tsx стартуют
    // ДО mount'а root → их ошибка может прийти раньше подписки).
    for (const q of queryClient.getQueryCache().getAll()) {
      if (q.state.status === 'error') handleError(q.state.error);
    }

    const unsubQ = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') {
        handleError(event.action.error);
      }
    });
    const unsubM = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.action?.type === 'error') {
        handleError(event.action.error);
      }
    });
    return () => {
      unsubQ();
      unsubM();
    };
  }, []);

  const currentPath = useRouterState({ select: (s) => s.location.pathname });
  // Экран для аналитики владельца: id роута (`/checkout/$planId`), а не адрес, иначе
  // каждый тариф стал бы отдельным экраном.
  const routeId = useRouterState({ select: (s) => s.matches[s.matches.length - 1]?.routeId });
  useEffect(() => {
    if (routeId) track('screen_view', { route: routeId });
  }, [routeId]);

  if (subGate) {
    return (
      <SubscriptionGateScreen
        channels={subGate}
        onRetry={() => {
          queryClient.clear();
          setSubGate(null);
        }}
      />
    );
  }

  if (tgError) {
    return <TelegramUnavailableScreen reason={tgError.reason} />;
  }

  if (webappOff) {
    return <WebappDisabledScreen />;
  }

  if (botDisabled) {
    return <BotDisabledScreen />;
  }

  return (
    <PersistQueryClientProvider
      client={queryClient}
      // В buster входит bot_id: адрес мини-аппа общий у всех ботов, и кэш
      // тарифов или профиля одного бота успевал отрисоваться в другом до первого
      // рефетча. При несовпадении persist сам выбрасывает сохранённый кэш.
      persistOptions={{
        persister: persister!,
        maxAge: 24 * 60 * 60 * 1000,
        buster: `${APP_VERSION}:${currentBotId() ?? 'none'}`,
      }}
    >
      <div
        className="min-h-screen bg-background text-foreground"
        style={{
          // Вверху страницы два разных отступа, и складывать их напрямую нельзя.
          // `env(safe-area-inset-top)` это вырез экрана, но в вебвью Telegram он
          // на части устройств приходит нулём — там то же значение отдаётся
          // переменной `--tg-safe-area-inset-top`. Это ОДНО И ТО ЖЕ, поэтому
          // берём max, а не сумму: сумма даёт двойной отступ на айфоне.
          // `--tg-content-safe-area-inset-top` — своя шапка Telegram («✕ Закрыть»,
          // ⌄, ⋮), она рисуется ПОВЕРХ контента в fullscreen, и вот её нужно
          // прибавлять. Без неё верх страницы уезжает под шапку (заявка #56).
          paddingTop:
            'calc(max(env(safe-area-inset-top), var(--tg-safe-area-inset-top, 0px)) + var(--tg-content-safe-area-inset-top, 0px) + 8px)',
          // Внизу тот же принцип: навигация стоит на `env(safe-area-inset-bottom) + 12px`,
          // а запас под контентом был числом без учёта этого отступа. На устройствах с
          // большим системным отступом (жестовая навигация) пилюля поднималась выше и
          // накрывала последний ряд контента (заявка #69).
          paddingBottom: 'calc(env(safe-area-inset-bottom) + 110px)',
        }}
      >
        <div className="w-full max-w-md mx-auto">
          <Outlet />
        </div>

        <nav
          aria-hidden={keyboardOpen}
          className={`fixed left-1/2 -translate-x-1/2 z-50 flex items-center gap-0.5 min-[360px]:gap-1 p-2 rounded-full [backdrop-filter:blur(20px)_saturate(160%)] [-webkit-backdrop-filter:blur(20px)_saturate(160%)] transform-gpu [contain:paint] transition-opacity duration-150 ${
            keyboardOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
          style={{
            bottom: 'calc(env(safe-area-inset-bottom) + 12px)',
            background: 'var(--calm-nav-bg)',
            border: '1px solid var(--calm-nav-border)',
            boxShadow: 'var(--calm-nav-shadow)',
          }}
        >
          <TabLink to="/me" icon={<LayoutDashboard size={24} />} label={t('Home')} currentPath={currentPath} />
          <TabLink to="/plans" icon={<Zap size={24} />} label={t('Plans')} currentPath={currentPath} />
          <TabLink to="/activity" icon={<Activity size={24} />} label={t('Activity')} currentPath={currentPath} />
          {/* Партнёр может выключить отзывы целиком. Раньше вкладка оставалась и вела
              на «Владелец бота скрыл отзывы» — выглядело как поломка мини-аппа. */}
          {api.getAuth().reviewsEnabled && (
            <TabLink to="/reviews" icon={<Star size={24} />} label={t('Reviews')} currentPath={currentPath} />
          )}
          <TabLink to="/profile" icon={<User size={24} />} label={t('Profile')} currentPath={currentPath} />
        </nav>

        {/* Тосты сверху. Снизу они ложились на кнопки шторок («Заморозить»,
            «Отмена»), а вечный тост об ошибке сервера закрывал их, пока его не смахнут.
            Отступ снизу к тому же не работал на телефоне: sonner ниже 600px берёт
            `mobileOffset`, а не `offset`. Сверху отступ тот же, что у страницы, чтобы
            не залезть под шапку Telegram в полноэкранном режиме. */}
        <Toaster
          position="top-center"
          /* Интерфейс принудительно тёмный (см. index.css), а "system" отдавал белые
             тосты тем, у кого светлая тема ОС. */
          theme="dark"
          richColors
          offset={{ top: TOAST_TOP }}
          mobileOffset={{ top: TOAST_TOP }}
        />

        <ErrorReportSheet
          error={reportSheetError}
          open={reportSheetError !== null}
          onClose={() => setReportSheetError(null)}
        />
      </div>
    </PersistQueryClientProvider>
  );
}

/** Системная настройка «уменьшить движение» — плавный скролл ей противопоказан. */
function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

function TabLink({ to, icon, label, currentPath }: { to: string; icon: React.ReactNode; label: string; currentPath: string }) {
  const isActive =
    currentPath === to ||
    (to === '/' && currentPath === '/me') ||
    (to === '/me' && currentPath === '/');
  return (
    <Link
      to={to}
      preload="intent"
      viewTransition
      onClick={(e) => {
        haptic('light');
        // Повторный тап по вкладке, на которой уже стоишь, раньше не делал ничего.
        // Теперь это точка возврата: наверх списка + подтянуть свежие данные.
        if (isActive) {
          e.preventDefault();
          window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
          void queryClient.refetchQueries({ type: 'active' });
        }
      }}
      className={`flex items-center justify-center gap-2 rounded-full transition-all duration-200 active:scale-95 ${
        isActive
          ? 'bg-primary/20 text-accent px-4 min-[360px]:px-5 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.15)]'
          : 'text-white/50 p-2.5 min-[360px]:p-3 active:text-white'
      }`}
    >
      {icon}
      {/* С пятой вкладкой (отзывы) пилюля на 320px шире экрана и обрезалась по краям.
          Ниже 360px показываем только иконки: подпись там всё равно первой идёт под нож. */}
      {isActive && <span className="hidden min-[360px]:inline text-sm font-semibold whitespace-nowrap">{label}</span>}
    </Link>
  );
}

function SubscriptionGateScreen({
  channels,
  onRetry,
}: {
  channels: { url?: string; name?: string }[];
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center space-y-4">
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center"
        style={{ background: 'rgb(var(--brand-primary-rgb) / 0.12)' }}
      >
        <Megaphone size={28} strokeWidth={1.8} color="var(--color-accent)" />
      </div>
      <h2 className="text-2xl font-bold text-foreground">{t('Subscribe to continue')}</h2>
      <p className="text-[#8a8c93] max-w-sm">
        {channels.length === 1 ? t('Subscribe channels one') : t('Subscribe channels many')}
      </p>
      <div className="w-full max-w-sm space-y-2">
        {channels.map((c, i) =>
          c.url ? (
            <a
              key={i}
              href={c.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => haptic('light')}
              className="flex items-center justify-center gap-2 w-full calm-cta px-6 py-3 rounded-xl text-sm font-bold"
            >
              <Megaphone size={16} /> {c.name || t('Channel')}
            </a>
          ) : null,
        )}
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="flex items-center justify-center gap-2 w-full max-w-sm bg-white/8 text-foreground px-6 py-3 rounded-xl text-sm font-bold active:scale-[0.98] transition-transform"
      >
        <Check size={16} /> {t('I subscribed')}
      </button>
    </div>
  );
}
